/**
 * AddContext - Manages MediaRecorder lifecycle across page navigation.
 *
 * The provider lives above the Router in App.tsx so the recording
 * survives when the user navigates away from /record and back.
 *
 * Supports two modes:
 * - Batch mode (existing): MediaRecorder → blob → S3 upload → async pipeline
 * - Streaming mode (new): AudioWorklet → Transcribe Streaming + WebSocket persistence
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useStreamingTranscription, type SessionStatus } from '@/hooks/useStreamingTranscription';
import type { TranscriptFragment } from '@/services/transcribeStreamingService';
import type { StreamingSession } from '@/services/streamingSessionService';
import { indexedDBSessionService, type InterruptedRecording, type CapturedPhoto, type RecordingNote } from '@/services/indexedDBSessionService';
import { useCapabilities } from '@/contexts/CapabilitiesContext';
import { createLogger } from '@/lib/logger';

const log = createLogger('AddContext');

type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';
type TranscriptionMode = 'batch' | 'streaming';

interface RecordedBlob {
  blob: Blob;
  duration: number;
}

interface AddContextValue {
  // --- Batch mode (existing) ---
  recordingState: RecordingState;
  duration: number;
  error: string | null;
  recordedBlob: RecordedBlob | null;
  startRecording: (languageCode?: string) => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  discardRecording: () => void;
  clearRecordedBlob: () => void;
  getAnalyserNode: () => AnalyserNode | null;
  resumeFromInterruptedRecording: (recording: InterruptedRecording) => Promise<void>;

  // --- Photos and Notes ---
  photos: CapturedPhoto[];
  notes: RecordingNote[];
  addPhoto: (blob: Blob) => void;
  removePhoto: (photoId: string) => void;
  addNote: (text: string) => void;
  removeNote: (noteId: string) => void;

  // --- Validation ---
  validateBeforeSubmit: () => string | null;

  // --- Draft restore ---
  restorePhotos: (photos: CapturedPhoto[]) => void;
  restoreNotes: (notes: RecordingNote[]) => void;

  // --- Plan-based limits ---
  maxDurationSeconds: number;

  // --- Streaming mode (new) ---
  transcriptionMode: TranscriptionMode;
  streaming: {
    sessionId: string | null;
    sessionStatus: SessionStatus;
    liveTranscript: string;
    finalizedTranscript: string;
    transcriptFragments: TranscriptFragment[];
    startStreaming: (languageCode: string) => Promise<void>;
    pauseStreaming: () => void;
    resumeStreaming: () => Promise<void>;
    stopStreaming: (params: {
      projectId: string;
      projectName: string;
      templateId: string;
      templateName: string;
      outputFormat: string;
      identityId: string;
    }) => Promise<string | null>;
    discardStreaming: () => void;
    isTranscribeConnected: boolean;
    isWebSocketConnected: boolean;
    interruptedSessions: StreamingSession[];
    loadInterruptedSessions: () => Promise<void>;
    resumeInterruptedSession: (sessionId: string, languageCode: string) => Promise<void>;
    duration: number;
    getAnalyserNode: () => AnalyserNode | null;
  };
}

const AddContext = createContext<AddContextValue | undefined>(undefined);

// Fallback while capabilities are loading (free tier limit)
const DEFAULT_MAX_DURATION_SECONDS = 30 * 60; // 30 minutes (free plan)

export const AddProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // --- Plan-based recording duration ---
  const { limits } = useCapabilities();
  const maxDurationSeconds = limits.maxRecordingMinutes === -1
    ? Number.MAX_SAFE_INTEGER
    : limits.maxRecordingMinutes * 60 || DEFAULT_MAX_DURATION_SECONDS;
  const maxDurationRef = useRef(maxDurationSeconds);
  useEffect(() => { maxDurationRef.current = maxDurationSeconds; }, [maxDurationSeconds]);
  // --- Transcription mode (auto-detected based on connectivity) ---
  const [transcriptionMode, setTranscriptionMode] = useState<TranscriptionMode>(
    navigator.onLine ? 'streaming' : 'batch'
  );

  // --- Batch mode state (existing) ---
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<RecordedBlob | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const durationRef = useRef<number>(0);
  const discardRef = useRef<boolean>(false);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  // Ref for stopRecording so the onstop handler and timer can access the latest version
  const stopRecordingRef = useRef<() => void>(() => {});

  // IndexedDB persistence state
  const sessionIdRef = useRef<string>('');
  const mimeTypeRef = useRef<string>('');
  const languageCodeRef = useRef<string>('en-US');
  const recordingStartedAtRef = useRef<number>(0);

  // Photos and Notes state
  const photosRef = useRef<CapturedPhoto[]>([]);
  const notesRef = useRef<RecordingNote[]>([]);
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [notes, setNotes] = useState<RecordingNote[]>([]);

  // --- Streaming mode hook ---
  const streaming = useStreamingTranscription();

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Save current recording state to IndexedDB
   */
  const saveToIndexedDB = useCallback(async () => {
    if (!sessionIdRef.current || chunksRef.current.length === 0) return;

    try {
      const recording: InterruptedRecording = {
        sessionId: sessionIdRef.current,
        chunks: [...chunksRef.current],
        mimeType: mimeTypeRef.current,
        duration: durationRef.current,
        languageCode: languageCodeRef.current,
        startedAt: recordingStartedAtRef.current,
        lastSavedAt: Date.now(),
        photos: photosRef.current.length > 0 ? [...photosRef.current] : undefined,
        notes: notesRef.current.length > 0 ? [...notesRef.current] : undefined,
      };

      await indexedDBSessionService.saveRecording(recording);
      log.debug('Recording saved to IndexedDB', {
        sessionId: sessionIdRef.current,
        chunks: chunksRef.current.length,
        duration: durationRef.current,
        photos: photosRef.current.length,
        notes: notesRef.current.length,
      });
    } catch (err) {
      log.error('Failed to save recording to IndexedDB', err as Error);
    }
  }, []);

  /**
   * Clear recording from IndexedDB
   */
  const clearFromIndexedDB = useCallback(async () => {
    if (!sessionIdRef.current) return;

    try {
      await indexedDBSessionService.deleteRecording(sessionIdRef.current);
      log.info('Recording cleared from IndexedDB', { sessionId: sessionIdRef.current });
    } catch (err) {
      log.error('Failed to clear recording from IndexedDB', err as Error);
    }
  }, []);

  /**
   * Add a photo to the current recording
   */
  const addPhoto = useCallback((blob: Blob) => {
    const photo: CapturedPhoto = {
      id: crypto.randomUUID(),
      blob,
      timestamp: durationRef.current,
      capturedAt: Date.now(),
    };
    photosRef.current.push(photo);
    setPhotos([...photosRef.current]);
    saveToIndexedDB();
    log.debug('Photo added', { photoId: photo.id, timestamp: photo.timestamp });
  }, [saveToIndexedDB]);

  /**
   * Remove a photo from the current recording
   */
  const removePhoto = useCallback((photoId: string) => {
    photosRef.current = photosRef.current.filter(p => p.id !== photoId);
    setPhotos([...photosRef.current]);
    saveToIndexedDB();
    log.debug('Photo removed', { photoId });
  }, [saveToIndexedDB]);

  /**
   * Add a note to the current recording
   */
  const addNote = useCallback((text: string) => {
    const note: RecordingNote = {
      id: crypto.randomUUID(),
      text,
      timestamp: durationRef.current,
      createdAt: Date.now(),
    };
    notesRef.current.push(note);
    setNotes([...notesRef.current]);
    saveToIndexedDB();
    log.debug('Note added', { noteId: note.id, timestamp: note.timestamp });
  }, [saveToIndexedDB]);

  /**
   * Remove a note from the current recording
   */
  const removeNote = useCallback((noteId: string) => {
    notesRef.current = notesRef.current.filter(n => n.id !== noteId);
    setNotes([...notesRef.current]);
    saveToIndexedDB();
    log.debug('Note removed', { noteId });
  }, [saveToIndexedDB]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      clearTimers();
      mediaRecorderRef.current.stop();
      setRecordingState('stopped');
      // Clear from IndexedDB after successful stop
      clearFromIndexedDB();
    }
  }, [clearTimers, clearFromIndexedDB]);

  // Keep ref in sync so timer callback can call the latest version
  stopRecordingRef.current = stopRecording;

  const startRecording = useCallback(async (languageCode: string = 'en-US') => {
    setError(null);
    setRecordedBlob(null);
    chunksRef.current = [];
    photosRef.current = [];
    notesRef.current = [];
    setPhotos([]);
    setNotes([]);

    // Generate session ID for IndexedDB persistence
    sessionIdRef.current = `recording-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    languageCodeRef.current = languageCode;
    recordingStartedAtRef.current = Date.now();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up audio analyser for level meter
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Create MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';
      mimeTypeRef.current = mimeType;

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      discardRef.current = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          // Save to IndexedDB after each chunk (every 1 second)
          saveToIndexedDB();
        }
      };

      mediaRecorder.onstop = () => {
        // Stop all tracks and close audio context
        stream.getTracks().forEach(track => track.stop());
        audioContext.close();
        streamRef.current = null;
        audioContextRef.current = null;
        analyserRef.current = null;

        if (!discardRef.current) {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          setRecordedBlob({ blob, duration: durationRef.current });
        }

        // Reset duration and state so the UI shows 0:00 for the next recording
        durationRef.current = 0;
        setDuration(0);
        setRecordingState('idle');
      };

      mediaRecorder.start(1000); // Collect data every second
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;
      durationRef.current = 0;
      setDuration(0);
      setRecordingState('recording');

      // Start timer
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000) + pausedDurationRef.current;
        durationRef.current = elapsed;
        setDuration(elapsed);

        if (elapsed >= maxDurationRef.current) {
          stopRecordingRef.current();
        }
      }, 1000);

    } catch (err) {
      console.error('Failed to start recording:', err);
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access and try again.');
      } else {
        setError('Failed to start recording. Please check your microphone.');
      }
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      pausedDurationRef.current = durationRef.current;
      clearTimers();
      setRecordingState('paused');
    }
  }, [clearTimers]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      startTimeRef.current = Date.now();
      setRecordingState('recording');

      // Resume timer
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000) + pausedDurationRef.current;
        durationRef.current = elapsed;
        setDuration(elapsed);

        if (elapsed >= maxDurationRef.current) {
          stopRecordingRef.current();
        }
      }, 1000);
    }
  }, []);

  const discardRecording = useCallback(() => {
    discardRef.current = true;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      clearTimers();
      mediaRecorderRef.current.stop();
    }
    chunksRef.current = [];
    photosRef.current = [];
    notesRef.current = [];
    durationRef.current = 0;
    setDuration(0);
    setPhotos([]);
    setNotes([]);
    setRecordingState('idle');
    setRecordedBlob(null);
    // Clear from IndexedDB
    clearFromIndexedDB();
  }, [clearTimers, clearFromIndexedDB]);

  const clearRecordedBlob = useCallback(() => {
    setRecordedBlob(null);
  }, []);

  const getAnalyserNode = useCallback(() => {
    return analyserRef.current;
  }, []);

  /**
   * Resume from an interrupted recording stored in IndexedDB
   */
  const resumeFromInterruptedRecording = useCallback(async (recording: InterruptedRecording) => {
    try {
      log.info('Resuming from interrupted recording', {
        sessionId: recording.sessionId,
        chunks: recording.chunks.length,
        duration: recording.duration
      });

      // Restore state
      chunksRef.current = [...recording.chunks];
      durationRef.current = recording.duration;
      setDuration(recording.duration);
      sessionIdRef.current = recording.sessionId;
      mimeTypeRef.current = recording.mimeType;
      languageCodeRef.current = recording.languageCode;
      recordingStartedAtRef.current = recording.startedAt;

      // Restore photos and notes
      photosRef.current = recording.photos ? [...recording.photos] : [];
      notesRef.current = recording.notes ? [...recording.notes] : [];
      setPhotos(photosRef.current);
      setNotes(notesRef.current);

      // Create blob from existing chunks and set as recorded
      const blob = new Blob(recording.chunks, { type: recording.mimeType });
      setRecordedBlob({ blob, duration: recording.duration });
      setRecordingState('stopped');

      log.info('Successfully resumed interrupted recording', {
        sessionId: recording.sessionId,
        duration: recording.duration,
        photos: photosRef.current.length,
        notes: notesRef.current.length,
      });
    } catch (err) {
      log.error('Failed to resume interrupted recording', err as Error);
      setError('Failed to resume recording. Please try again.');
    }
  }, []);

  // Warn the user and stop Transcribe before page refresh/close while recording is active
  useEffect(() => {
    const isRecording = recordingState === 'recording' || recordingState === 'paused';
    const isStreaming = streaming.sessionStatus === 'active' || streaming.sessionStatus === 'paused';

    if (!isRecording && !isStreaming) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Stop Transcribe streaming to prevent runaway AWS billing
      // The WebSocket disconnect handler will mark the session as 'interrupted' for recovery
      streaming.discardStreaming();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [recordingState, streaming.sessionStatus, streaming.discardStreaming]);

  // Auto-detect transcription mode based on network connectivity
  useEffect(() => {
    const handleOnline = () => {
      if (recordingState === 'idle' && streaming.sessionStatus === 'idle') {
        setTranscriptionMode('streaming');
        log.info('Network online — switched to streaming mode');
      }
    };
    const handleOffline = () => {
      if (recordingState === 'idle' && streaming.sessionStatus === 'idle') {
        setTranscriptionMode('batch');
        log.info('Network offline — switched to batch mode');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [recordingState, streaming.sessionStatus]);

  const validateBeforeSubmit = useCallback((): string | null => {
    if (!navigator.onLine) {
      return 'No internet connection';
    }
    return null;
  }, []);

  /**
   * Replace photos with restored draft photos
   */
  const restorePhotos = useCallback((restoredPhotos: CapturedPhoto[]) => {
    photosRef.current = [...restoredPhotos];
    setPhotos([...restoredPhotos]);
    log.info('Photos restored from draft', { count: restoredPhotos.length });
  }, []);

  /**
   * Replace notes with restored draft notes
   */
  const restoreNotes = useCallback((restoredNotes: RecordingNote[]) => {
    notesRef.current = [...restoredNotes];
    setNotes([...restoredNotes]);
    log.info('Notes restored from draft', { count: restoredNotes.length });
  }, []);

  const value: AddContextValue = {
    recordingState,
    duration,
    error,
    recordedBlob,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    discardRecording,
    clearRecordedBlob,
    getAnalyserNode,
    resumeFromInterruptedRecording,
    photos,
    notes,
    addPhoto,
    removePhoto,
    addNote,
    removeNote,
    validateBeforeSubmit,
    restorePhotos,
    restoreNotes,
    maxDurationSeconds,
    transcriptionMode,
    streaming,
  };

  return (
    <AddContext.Provider value={value}>
      {children}
    </AddContext.Provider>
  );
};

export function useAdd(): AddContextValue {
  const context = useContext(AddContext);
  if (!context) {
    throw new Error('useAdd must be used within a AddProvider');
  }
  return context;
}
